import { kebabCase } from 'lodash-es'

export default {
  inheritAttrs: false,
  props: {
    value: { default: '' },
    label: { type: String, default: '' },
    hideLabel: { type: Boolean, default: false },
    helpText: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    itemDefault: { type: Object, default: null },
    itemLabel: { type: String, default: 'name' },
    itemValue: { type: String, default: 'id' },
    validator: {
      type: Function,
      default: ({ validationErrors }) => {
        return validationErrors
      },
    },
    icon: { type: String, default: '' },
    iconAngle: { type: Number, default: 0 },
    iconSize: {
      type: String,
      default: 'md',
      validator: value => {
        // The value must match one of these strings
        return ['sm', 'md', 'lg'].indexOf(value) !== -1
      },
    },
    transparent: { type: Boolean, default: false },
    expanded: { type: Boolean, default: false },
    bordered: { type: Boolean, default: false },
    reversed: { type: Boolean, default: false },
    taggable: { type: Boolean, default: true },
  },
  data: () => ({
    isDirty: false,
    isActive: false,
    validationErrors: [],
  }),
  mounted() {
    this.validate(this.value)
  },

  computed: {
    kebabComponentName() {
      return kebabCase(this.$options.name).replace(/-([0-9])/g, '$1')
    },
    classes() {
      let classes = {}
      classes[`${this.kebabComponentName}`] = true
      classes[`${this.kebabComponentName}--hide-label`] = this.hideLabel
      classes[`${this.kebabComponentName}--is-valid`] = this.isValid
      classes[`${this.kebabComponentName}--has-errors`] = !this.isValid
      classes[`${this.kebabComponentName}--horizontal`] = this.horizontal
      classes[`${this.kebabComponentName}--transparent`] = this.transparent
      classes[`${this.kebabComponentName}--expanded`] = this.expanded
      classes[`${this.kebabComponentName}--bordered`] = this.bordered
      classes[`${this.kebabComponentName}--reversed`] = this.reversed
      classes[`${this.kebabComponentName}--taggable`] = this.taggable
      return classes
    },
    computedItems() {
      return this.items.map(i => {
        if (typeof i !== 'object') {
          let v = i
          i = {}
          i[this.itemLabel] = v
          i[this.itemValue] = v
        }
        return i
      })
    },
    isValid() {
      return this.validationErrors.length === 0
    },

    computedId() {
      return this.$attrs.id
        ? this.$attrs.id
        : `${this.kebabComponentName}_${Math.round(
            Math.random() * 10000000000000000
          )}`
    },

    selectedItem() {
      return this.computedItems.find(i => i[this.itemValue] === this.value)
    },

    selectedItemLabel() {
      if (!this.selectedItem && !this.itemDefault) return 'Please Select'
      return this.selectedItem
        ? this.selectedItem[this.itemLabel]
        : this.itemDefault[this.itemLabel]
    },

    computedHelpText() {
      if (
        this.$attrs.hasOwnProperty('maxlength') &&
        Number(this.$attrs.maxlength) > 0
      ) {
        return (
          this.helpText +
          `${this.value.length}/${Number(this.$attrs.maxlength)} characters.`
        )
      }
      return this.helpText
    },
    listeners() {
      // eslint-disable-next-line no-unused-vars

      const listeners = JSON.parse(JSON.stringify(this.$listeners))
      delete listeners.input
      return listeners
    },
  },
  watch: {
    value(value) {
      this.validate(value)
    },
  },
  methods: {
    validate(value) {
      // Clear validation errors
      this.validationErrors = []

      // Required validation
      if (
        this.$attrs.hasOwnProperty('required') &&
        this.$attrs.required.toString().toLowerCase() !== 'false'
      ) {
        if (!value) {
          this.validationErrors.push(`This field is required.`)
        }
      }

      // Validation when value is present
      if (value) {
        if (
          this.$attrs.hasOwnProperty('maxlength') &&
          Number(this.maxlength) > 0 &&
          value.length > Number(this.maxlength)
        ) {
          this.validationErrors.push(
            `${this.label || 'This field'} is too long.`
          )
        }
        // Trigger validator and store result
        const newValidationErrors = this.validator({
          value: value,
          validationErrors: this.validationErrors,
        })

        // Update validation errors
        this.validationErrors = newValidationErrors
      }
      // Emit validation event
      this.$emit('validated', this.validationErrors)
    },
    onFocus() {
      this.isActive = true
    },
    onBlur() {
      this.isDirty = true
      this.isActive = false
    },
    input(event) {
      this.isDirty = true
      this.validate(event.target.value)
      this.$emit('input', event.target.value)
    },
  },
}
